/**
 * ============================================================================
 *   Copyright (c) 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * ============================================================================
 *   Module       : Responsive
 *   Feature      : 197-portal-light-theme-responsive
 *   Description  : Small visibility toolkit that screens use to apply the
 *                  responsive rubric (desktop full / tablet condensed / mobile
 *                  quick-glance). Driven by `useViewport`. Per-screen
 *                  composition is a screen-by-screen decision; these helpers are
 *                  the shared mechanism so it stays consistent.
 *   Contract     : specs/197-portal-light-theme-responsive/contracts/responsive-rubric.contract.md
 *   Constitution : v1.19.0
 *   Author       : Peter Dobler
 *   Created      : 2026-06-13
 * ============================================================================
 */
import React from 'react';
import { useViewport, ViewportClass } from '../ui/useViewport';

/** Render children only on the given viewport class(es). */
export function ShowOn({
  when,
  children,
}: {
  when: ViewportClass[];
  children: React.ReactNode;
}) {
  const { class: vp } = useViewport();
  return when.includes(vp) ? <>{children}</> : null;
}

/** Render children except on the given viewport class(es). */
export function HideOn({
  when,
  children,
}: {
  when: ViewportClass[];
  children: React.ReactNode;
}) {
  const { class: vp } = useViewport();
  return when.includes(vp) ? null : <>{children}</>;
}

/**
 * Extended content (full tables / long lists / dense detail). Hidden on mobile
 * per the rubric — replace with a <QuickGlance> summary there.
 */
export function Extended({ children }: { children: React.ReactNode }) {
  return <HideOn when={['mobile']}>{children}</HideOn>;
}

/** Quick-glance content (key status / KPIs) — shown on mobile. */
export function QuickGlance({ children }: { children: React.ReactNode }) {
  return <ShowOn when={['mobile']}>{children}</ShowOn>;
}
